import type { ErrorDetails } from "@esi/shared";
import type { ClientResponse } from "hono/client";
import type { SuccessStatusCode } from "hono/utils/http-status";
import { errorCopy, resolveErrorCode } from "./error-copy.ts";
import { currentLocale, t } from "./i18n/i18n.ts";

/** A refused API call with locale-appropriate copy and structured server metadata. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    /** The server supplied an explanation: distinguishes a refusal from a route miss. */
    readonly explained = false,
    /** Counts and configuration labels used by a refusal's catalogue template. */
    readonly details?: ErrorDetails,
    /** Model roles a refusal names (`roles` in the body: `model_roles_uncovered`). */
    readonly roles?: string[],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Reads a non-OK response into an `ApiError` with a message fit for the user. */
export async function apiError(res: {
  status: number;
  json(): Promise<unknown>;
}): Promise<ApiError> {
  const body = (await res.json().catch(() => null)) as {
    code?: unknown;
    message?: unknown;
    details?: unknown;
    roles?: unknown;
  } | null;
  const legacyMessage = typeof body?.message === "string" ? body.message : undefined;
  const code = resolveErrorCode(
    typeof body?.code === "string" ? body.code : undefined,
    legacyMessage,
  );
  const details =
    typeof body?.details === "object" && body.details !== null
      ? (body.details as ErrorDetails)
      : undefined;
  const roles = Array.isArray(body?.roles)
    ? body.roles.filter((r): r is string => typeof r === "string")
    : undefined;
  const message = () => {
    if (res.status >= 500) {
      return statusMessage(res.status);
    }
    return (
      errorCopy(code, legacyMessage, details) ??
      (currentLocale() === "en" && legacyMessage ? legacyMessage : statusMessage(res.status))
    );
  };
  const error = new ApiError(
    res.status,
    message(),
    code,
    res.status < 500 && Boolean(legacyMessage),
    details,
    roles,
  );
  // Cached query errors follow a language switch too; raw server copy stays inside this closure.
  Object.defineProperty(error, "message", {
    configurable: true,
    get: message,
  });
  return error;
}

/** A plain explanation of a refused request, in the active language. */
export function statusMessage(status: number): string {
  if (status === 401) {
    return t("errors.sessionEnded");
  }
  if (status === 403) {
    return t("errors.forbidden");
  }
  if (status === 404) {
    return t("errors.notFound");
  }
  if (status === 501) {
    return t("errors.notImplemented");
  }
  return status >= 500 ? t("errors.server") : t("errors.refused");
}

/** The body type of the success statuses of an RPC response union. */
type SuccessBody<R> =
  R extends ClientResponse<infer T, infer S, "json">
    ? S extends SuccessStatusCode
      ? T
      : never
    : never;

/** Parses an OK response, or throws its `ApiError`. */
export async function ok<R extends ClientResponse<unknown, number, "json">>(
  res: R,
): Promise<SuccessBody<R>> {
  if (!res.ok) {
    throw await apiError(res);
  }
  try {
    return (await res.json()) as SuccessBody<R>;
  } catch {
    // Not the API's JSON (an HTML error page in front of it, say): never treat it as success.
    throw new ApiError(res.status, t("errors.unexpected"));
  }
}

/** A security action refused until the session re-authenticated (5 minutes). */
export const needsReauth = (error: unknown) =>
  error instanceof ApiError && error.code === "reauth_required";

/** A `204` answer, or the call's `ApiError`. */
export async function noContent(res: {
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
}): Promise<void> {
  if (!res.ok) {
    throw await apiError(res);
  }
}
